import _ from 'lodash'
import {walk} from '../../build/json-walker'
import {DATA_TYPES} from '../../common/namespaceMapping'
import {DataNode, PropDataNode} from '../../wixShared.json'
import type {SchemaFile} from '@wix/document-services-types'
import {isObjectSchema} from '../../common/schemaConversionUtils'

const DEREFERENCE = {
    $ref: (
        value: string,
        key: string,
        parentNode: object,
        ctx: DereferenceContext & {refChain?: Set<string>; memo?: Map<string, object>}
    ) => {
        //sometimes we receive $ref: 'some_file.json#/SchemaName' and sometimes just 'SchemaName' with the assumption it is in same map
        const schemaName = value.split('#/').pop() as string

        // Initialize refChain if needed
        ctx.refChain ??= new Set([ctx.name])
        ctx.memo ??= new Map()

        // If this reference would create a cycle, preserve it
        if (ctx.refChain.has(schemaName)) {
            return
        }

        const referredSchema = ctx.ns[schemaName] ?? ctx.common[schemaName]
        // TODO: we need to fix this file to either
        // 1. dereference properly and find the schema in all common schema maps
        // 2. throw an error if given a reference without the fully qualified urn
        // currently, if we pass here and delete the $ref without actual dereferencing, this is a bug because the schema is empty
        if (!referredSchema && value.includes('urn:dm:schemas:')) {
            return
        }
        delete parentNode[key]

        // Add this schema to refChain before recursing
        ctx.refChain.add(schemaName)
        const dereferencedReferredSchema =
            ctx.memo.get(schemaName) ?? walk(DEREFERENCE, _.cloneDeep(referredSchema), ctx)
        // Remove it after we're done with this branch
        ctx.refChain.delete(schemaName)

        _.assign(parentNode, _.cloneDeep(dereferencedReferredSchema))
        ctx.memo.set(schemaName, dereferencedReferredSchema)
    }
}
interface DereferenceContext {
    ns: object //all schemas in namespace of schema we are dereferencing
    common: object //all common schemas we can reference
    name: string //name of schema being dereferenced
}
export const dereference = (schema: object, ctx: DereferenceContext) => walk(DEREFERENCE, _.cloneDeep(schema), ctx)

export const combineAllOf = (schema: any): any => {
    if (schema.allOf) {
        const areAllObjects = schema.allOf.every((_schema: any) => isObjectSchema(_schema))
        if (!areAllObjects) {
            return schema
        }
        const required = _.union(
            _.flatMap(schema.allOf, item => item.required || []),
            schema.required || []
        )

        const combinedSubSchemas = _.map(schema.allOf, subschema => combineAllOf(subschema))
        const combined: any = {
            ..._.omit(schema, 'allOf'),
            type: 'object',
            properties: _.assign({}, ..._.map(combinedSubSchemas, 'properties'))
        }
        const shouldRestrict = _.some(combinedSubSchemas, subSchema => subSchema.additionalProperties === false)
        if (shouldRestrict) {
            combined.additionalProperties = false
        }
        if (required.length) {
            combined.required = required
        }
        return combined
    }

    if (schema.items) {
        return {...schema, items: combineAllOf(schema.items)}
    }

    if (schema.properties) {
        return {...schema, properties: _.mapValues(schema.properties, combineAllOf)}
    }
    return schema
}

interface SchemaLike {
    properties?: object
    allOf?: object[]
}

const combineSchemaWithDataNode = (schema: SchemaLike, dataNodeSchema: SchemaLike) => {
    if (schema.properties) {
        return {...schema, properties: {...schema.properties, ...dataNodeSchema.properties}}
    }

    if (schema.allOf) {
        const allOf = schema.allOf.slice()
        allOf.push(dataNodeSchema)
        return {...schema, allOf}
    }

    return schema
}

const DEFAULT_SCHEMA_EXTENSION = {
    [DATA_TYPES.prop]: PropDataNode,
    default: DataNode
}

export const extendDataNodeSchemas = (schemas: any, namespace: string): Record<string, SchemaFile> => {
    const nodeExtension = DEFAULT_SCHEMA_EXTENSION[namespace] ?? DEFAULT_SCHEMA_EXTENSION.default
    return _.mapValues(schemas, schema => combineSchemaWithDataNode(schema, nodeExtension)) as unknown as Record<
        string,
        SchemaFile
    >
}
